'use client'

import Link from 'next/link'
import { FileText, Lock, FolderPlus, Search, Pin } from 'lucide-react'

interface EmptyStateProps {
  variant: 'no-prompts' | 'no-public-prompts' | 'no-public-prompts-owner' | 'no-collections' | 'no-results' | 'no-pinned'
  onPrimaryAction?: () => void
  onSecondaryAction?: () => void
}

const EMPTY_STATES = {
  'no-prompts': {
    icon: FileText,
    headline: 'No prompts yet',
    body: '(in this collection)',
    primaryLabel: 'Create Prompt',
    secondaryLabel: 'Browse All',
  },
  'no-public-prompts': {
    icon: Lock,
    headline: 'No public prompts yet',
    body: "(This creator hasn't shared any prompts.)",
    primaryLabel: null,
    secondaryLabel: 'Back to Profile',
  },
  'no-public-prompts-owner': {
    icon: FileText,
    headline: 'No public prompts yet',
    body: 'Publish prompts from your Vault to showcase them here.',
    primaryLabel: 'Open Vault',
    secondaryLabel: null,
  },
  'no-collections': {
    icon: FolderPlus,
    headline: 'No custom collections yet',
    body: 'Create one to organize your prompts.',
    primaryLabel: 'Create Collection',
    secondaryLabel: null,
  },
  'no-results': {
    icon: Search,
    headline: 'No matches found',
    body: 'Try different tags or keywords.',
    primaryLabel: 'Clear Filters',
    secondaryLabel: 'Browse All',
  },
  'no-pinned': {
    icon: Pin,
    headline: 'No pinned prompts yet',
    body: 'Pin your favorites to the top.',
    primaryLabel: 'Explore',
    secondaryLabel: null,
  },
}

/**
 * Empty state component for various scenarios.
 * Shows: illustration icon, headline, body text, primary + secondary CTAs.
 */
export function EmptyState({ variant, onPrimaryAction, onSecondaryAction }: EmptyStateProps) {
  const state = EMPTY_STATES[variant]
  const Icon = state.icon

  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <div className="mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-[var(--color-bg-elevated)]">
        <Icon className="h-10 w-10 text-[var(--color-text-muted)]" />
      </div>

      <h3 className="mb-1 text-base font-semibold text-[var(--color-text-primary)]">
        {state.headline}
      </h3>
      <p className="mb-6 max-w-[280px] text-sm text-[var(--color-text-secondary)]">
        {state.body}
      </p>

      <div className="flex flex-col items-center gap-2">
        {variant === 'no-public-prompts-owner' ? (
          <Link
            href="/vault"
            className="rounded-[var(--radius-md)] bg-[var(--color-brand-indigo)] px-6 py-2 text-sm font-medium text-white transition-colors hover:bg-[var(--color-brand-indigo)]/90"
          >
            Open Vault
          </Link>
        ) : (
          <>
            {state.primaryLabel && onPrimaryAction && (
              <button
                onClick={onPrimaryAction}
                className="rounded-[var(--radius-md)] bg-[var(--color-brand-indigo)] px-6 py-2 text-sm font-medium text-white transition-colors hover:bg-[var(--color-brand-indigo)]/90"
              >
                {state.primaryLabel}
              </button>
            )}
            {state.secondaryLabel && onSecondaryAction && (
              <button
                onClick={onSecondaryAction}
                className="text-sm text-[var(--color-text-secondary)] underline hover:text-[var(--color-text-primary)]"
              >
                {state.secondaryLabel}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  )
}
